import _ from 'lodash';

import isCustomRangeInput from 'isCustomRangeInput';

import { isLegacyMatrixTable } from './buildLegacyMatrixDefinition';

// How a dimension groups: a text or yes/no field's terms, a number's range
// buckets, or the day of the week or month
export type DimensionKind =
  | 'text'
  | 'boolean'
  | 'ranges'
  | 'dayOfWeek'
  | 'month';

export const getDimensionKind = (
  options: Studio.DimensionOptions | undefined,
): DimensionKind => {
  if (!options) {
    return 'text';
  }
  if (options.grouping) {
    return options.grouping;
  }
  if (options.ranges) {
    return 'ranges';
  }
  return options.fieldType === 'boolean' ? 'boolean' : 'text';
};

// Terms can be sorted and limited; ranges, days and months group without
// them (see buildGroupBy)
export const isTermKind = (kind: DimensionKind): boolean =>
  kind === 'text' || kind === 'boolean';

// Whether the block's first dimension groups by terms
export const hasTermDimension = (
  content: Pick<Studio.ChartContent, 'dimensions' | 'dimensionOptions'>,
): boolean => {
  const [dimA] = content.dimensions ?? [];
  return (
    !!dimA &&
    isTermKind(getDimensionKind((content.dimensionOptions ?? {})[dimA]))
  );
};

// A custom bucket's id is only for the classic editor's list; numbered, so
// rebuilding gives the same ones
const toRangeInput = (
  ranges: Studio.DimensionRanges,
): CustomDimensionRangeInput | FixedDimensionRangeInput =>
  ranges.mode === 'fixed'
    ? { mode: 'fixed', fixed: ranges.width }
    : {
        mode: 'custom',
        items: ranges.buckets.map(
          ({ label, from, to }, index): CustomDimensionInputItem => ({
            id: `bucket-${index + 1}`,
            label,
            ...(from !== undefined ? { from } : {}),
            ...(to !== undefined ? { to } : {}),
          }),
        ),
      };

// A dimension as the classic editor writes it. Day of week and month are
// the classic editor's own groupings, without a field type, and need the
// chart's matching flag too (see toGroupingFlags).
export const toDimension = ({
  field,
  options,
}: {
  field: string;
  options?: Studio.DimensionOptions;
}): Dimension => {
  const kind = getDimensionKind(options);
  if (kind === 'dayOfWeek' || kind === 'month') {
    return {
      field,
      isGroupByDayOfWeek: kind === 'dayOfWeek',
      isGroupByMonth: kind === 'month',
    };
  }
  const base: Dimension = {
    field,
    fieldType: 'text',
    isGroupByDayOfWeek: false,
    isGroupByMonth: false,
  };
  if (kind === 'boolean') {
    return { ...base, fieldType: 'boolean' };
  }
  if (kind === 'ranges' && options && options.ranges) {
    return {
      ...base,
      fieldType: options.fieldType ?? 'float',
      rangeInput: toRangeInput(options.ranges),
    };
  }
  return base;
};

// The chart's day-of-week and month flags, for its dimensions that group by
// them
export const toGroupingFlags = (
  dimensions: Dimension[],
): Pick<V5ChartDefinition, 'groupByDayOfWeek' | 'groupByMonth'> =>
  _.omitBy(
    {
      groupByDayOfWeek:
        dimensions.some((d) => d.isGroupByDayOfWeek) || undefined,
      groupByMonth: dimensions.some((d) => d.isGroupByMonth) || undefined,
    },
    _.isUndefined,
  );

// A saved dimension as a Studio dimension and its options. Undefined for
// one Studio doesn't group by (e.g. an int field, or a number without
// ranges, which the query doesn't group by at all).
export const readDimension = (
  dimension: Dimension,
): { field: string; options?: Studio.DimensionOptions } | undefined => {
  const { field, fieldType, rangeInput } = dimension;
  if (!fieldType) {
    if (field === 'dayOfWeek' && dimension.isGroupByDayOfWeek) {
      return { field, options: { grouping: 'dayOfWeek' } };
    }
    if (field === 'month' && dimension.isGroupByMonth) {
      return { field, options: { grouping: 'month' } };
    }
    return undefined;
  }
  if (fieldType === 'text') {
    return { field };
  }
  if (fieldType === 'boolean') {
    return { field, options: { fieldType } };
  }
  if ((fieldType === 'float' || fieldType === 'long') && rangeInput) {
    const ranges: Studio.DimensionRanges = isCustomRangeInput(rangeInput)
      ? {
          mode: 'custom',
          buckets: rangeInput.items.map(({ label, from, to }) => ({
            label,
            ...(from !== undefined ? { from } : {}),
            ...(to !== undefined ? { to } : {}),
          })),
        }
      : { mode: 'fixed', width: rangeInput.fixed };
    return { field, options: { fieldType, ranges } };
  }
  return undefined;
};

// Charts that draw one dimension's terms (or buckets) as their categories
const CATEGORY_CHARTS: Studio.VisType[] = [
  'bar',
  'combo',
  'radar',
  'waterfall',
  'pie',
  'treeMap',
  'funnel',
  'packedBubble',
];

// Cross-tab charts, whose second dimension groups by terms as a sub-group
const CROSS_TAB_CHARTS: Studio.VisType[] = [
  'stackedBar',
  'heatmap',
  'sankey',
  'table',
];

// What a chart can group its first (position 0) or second dimension by,
// following what its renderer draws: ranges on category charts, scatter,
// bubble and Table rows; day of week where it's ordered (category charts
// and Tables); month on Tables only; yes/no fields wherever terms are
// grouped, bar a split Line. A Table draws two dimensions at most: rows and
// columns, or rows of pairs.
export const getOfferedKinds = ({
  visType,
  table,
  position,
}: {
  visType: Studio.VisType | undefined;
  table?: Studio.TableOptions;
  position: number;
}): DimensionKind[] => {
  const type = visType ?? 'number';
  // Date comparison and pace tables group their rows by one text field
  if (type === 'table' && table && isLegacyMatrixTable({ visType, table })) {
    return position === 0 ? ['text'] : [];
  }
  if (type === 'table' && position > 1) {
    return [];
  }
  // A Map's second dimension breaks its regions' tooltips down by its terms;
  // it draws no third
  if (type === 'regionMap' && position > 1) {
    return [];
  }
  if (position > 0) {
    return CROSS_TAB_CHARTS.includes(type) ? ['text', 'boolean'] : ['text'];
  }
  if (type === 'table') {
    return ['text', 'boolean', 'ranges', 'dayOfWeek', 'month'];
  }
  if (CATEGORY_CHARTS.includes(type)) {
    return ['text', 'boolean', 'ranges', 'dayOfWeek'];
  }
  if (type === 'scatter') {
    return ['text', 'boolean', 'ranges'];
  }
  if (type === 'line') {
    return ['text'];
  }
  return ['text', 'boolean'];
};
